import React, { useEffect, useState } from "react";

interface EmailMockUpProps {
  subject?: string;
  previewText?: string;
  senderName?: string;
  senderEmail?: string;
  htmlContent?: string;
  plainText?: string;
  campaignData?: any;
  vars?: Record<string, string>;
  template?: {
    originalContent?: string;
    variables?: { index: number; example?: string }[];
  };
}

export const EmailMockUp: React.FC<EmailMockUpProps> = ({
  subject = "Valentine's Day Packages are here!",
  previewText,
  senderName,
  senderEmail,
  htmlContent,
  plainText,
  vars = {},
}) => {
  const [displayHtml, setDisplayHtml] = useState<string>("");

  // Resolve {{variable_name}} and {{1}} patterns in any string
  const resolveVars = (text: string): string => {
    return text.replace(/{{([^}]+)}}/g, (original, key) => {
      const trimmed = key.trim();

      // Numbered variable: {{1}}, {{2}}
      if (/^\d+$/.test(trimmed)) {
        const value = vars?.[`variable_${trimmed}`]?.trim();
        return value || "";
      }

      // Named variable: {{first_name}}
      const value = vars?.[trimmed]?.trim();
      return value || "";
    });
  };

  // Process HTML to make it responsive
  const processHtmlContent = (html: string): string => {
    let processed = html;

    // 1. Add viewport meta tag for proper scaling
    // (This is already in the template but ensure it's present)
    
    // 2. Make images responsive
    processed = processed.replace(
      /<img([^>]*)>/g,
      (match, attrs) => {
        // Add responsive styles if not already present
        if (!attrs.includes('style=')) {
          return `<img${attrs} style="max-width: 100%; height: auto; display: block;" />`;
        }
        // Add max-width and height auto to existing styles
        return match.replace(
          /style="([^"]*)"/,
          (styleMatch, styles) => {
            const responsiveStyles = `max-width: 100%; height: auto; ${styles}`;
            return `style="${responsiveStyles}"`;
          }
        );
      }
    );

    // 3. Make tables responsive
    processed = processed.replace(
      /<table([^>]*)>/g,
      (match, attrs) => {
        if (!attrs.includes('style=')) {
          return `<table${attrs} style="width: 100%; max-width: 100%; table-layout: fixed;" />`;
        }
        return match.replace(
          /style="([^"]*)"/,
          (styleMatch, styles) => {
            const responsiveStyles = `width: 100%; max-width: 100%; table-layout: fixed; ${styles}`;
            return `style="${responsiveStyles}"`;
          }
        );
      }
    );

    // 4. Make table cells responsive
    processed = processed.replace(
      /<(td|th)([^>]*)>/g,
      (match, tag, attrs) => {
        if (!attrs.includes('style=')) {
          return `<${tag}${attrs} style="word-wrap: break-word; overflow-wrap: break-word;" />`;
        }
        return match.replace(
          /style="([^"]*)"/,
          (styleMatch, styles) => {
            const responsiveStyles = `word-wrap: break-word; overflow-wrap: break-word; ${styles}`;
            return `style="${responsiveStyles}"`;
          }
        );
      }
    );

    // 5. Make divs and containers responsive
    processed = processed.replace(
      /<(div|section|article|main)([^>]*)>/g,
      (match, tag, attrs) => {
        if (!attrs.includes('style=')) {
          return `<${tag}${attrs} style="max-width: 100%; overflow-wrap: break-word;" />`;
        }
        return match.replace(
          /style="([^"]*)"/,
          (styleMatch, styles) => {
            const responsiveStyles = `max-width: 100%; overflow-wrap: break-word; ${styles}`;
            return `style="${responsiveStyles}"`;
          }
        );
      }
    );

    // 6. Handle paragraphs and text
    processed = processed.replace(
      /<(p|span|h1|h2|h3|h4|h5|h6)([^>]*)>/g,
      (match, tag, attrs) => {
        if (!attrs.includes('style=')) {
          return `<${tag}${attrs} style="word-wrap: break-word; overflow-wrap: break-word;" />`;
        }
        return match.replace(
          /style="([^"]*)"/,
          (styleMatch, styles) => {
            const responsiveStyles = `word-wrap: break-word; overflow-wrap: break-word; ${styles}`;
            return `style="${responsiveStyles}"`;
          }
        );
      }
    );

    // 7. Make iframes/videos responsive
    processed = processed.replace(
      /<(iframe|video)([^>]*)>/g,
      (match, tag, attrs) => {
        if (!attrs.includes('style=')) {
          return `<${tag}${attrs} style="max-width: 100%; width: 100%; height: auto;" />`;
        }
        return match.replace(
          /style="([^"]*)"/,
          (styleMatch, styles) => {
            const responsiveStyles = `max-width: 100%; width: 100%; height: auto; ${styles}`;
            return `style="${responsiveStyles}"`;
          }
        );
      }
    );

    // 8. Handle buttons
    processed = processed.replace(
      /<(button|a)([^>]*)>/g,
      (match, tag, attrs) => {
        if (attrs.includes('href=') || attrs.includes('button')) {
          if (!attrs.includes('style=')) {
            return `<${tag}${attrs} style="display: inline-block; max-width: 100%; word-wrap: break-word;" />`;
          }
          return match.replace(
            /style="([^"]*)"/,
            (styleMatch, styles) => {
              const responsiveStyles = `display: inline-block; max-width: 100%; word-wrap: break-word; ${styles}`;
              return `style="${responsiveStyles}"`;
            }
          );
        }
        return match;
      }
    );

    // 9. Add a container wrapper with responsive styles
    processed = `
      <div style="max-width: 100%; overflow-x: auto; -webkit-overflow-scrolling: touch; padding: 0; margin: 0;">
        <div style="max-width: 100%; margin: 0 auto; word-wrap: break-word; overflow-wrap: break-word;">
          ${processed}
        </div>
      </div>
    `;

    return processed;
  };

  useEffect(() => {
    let raw = "";

    if (htmlContent) {
      raw = htmlContent;
    } else if (plainText) {
      raw = `<p style="white-space: pre-wrap;">${plainText.replace(/\n/g, "<br>")}</p>`;
    } else {
      raw = "<p>Your email content will appear here...</p>";
    }

    // Resolve variables first
    let resolved = resolveVars(raw);
    
    // Then process for responsiveness
    resolved = processHtmlContent(resolved);
    
    setDisplayHtml(resolved);
  }, [htmlContent, plainText, vars]);

  const initial = senderName ? senderName[0] : "";

  return (
    <div className="mx-auto w-full max-w-[100%]">
      <div className="relative bg-[#F6F6F6] rounded-[40px] overflow-hidden border-[8px] border-[#F6F6F6] shadow-2xl">
        {/* Status Bar */}
        <div className="h-7 bg-white shadow flex items-center justify-between px-4 relative">
          <div className="absolute inset-x-0 flex justify-center">
            <div className="w-16 h-[5px] bg-[#333333] rounded-full mt-2" />
          </div>
          <span className="text-gray-700 text-[10px] font-medium z-10">9:41</span>
          <div className="flex items-center gap-1 z-10">
            <span className="text-gray-700 text-[10px]">WiFi</span>
          </div>
        </div>

        {/* Email App Header */}
        <div className="bg-white border-b border-gray-200 px-3 py-2 flex items-center gap-2">
          <button className="text-gray-700 text-xl pr-2">←</button>
          <div className="flex-1">
            <p className="text-sm font-semibold text-gray-900">Inbox</p>
          </div>
          <div className="text-[#7B7B7B] font-semibold">⋯</div>
        </div>

        {/* Email Content Area */}
        <div className="bg-white h-[380px] overflow-y-auto">
          {/* Email Header Info */}
          <div className="px-4 pt-4 pb-3 border-b border-gray-100">
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white font-medium shrink-0">
                {initial}
              </div>

              <div className="flex-1 min-w-0">
                {senderName && (
                  <p className="font-semibold text-gray-900 text-[13px] leading-tight">
                    {senderName}
                  </p>
                )}
                {senderEmail && (
                  <p className="text-gray-500 text-xs truncate">{senderEmail}</p>
                )}
                <p className="text-[10px] text-[#7B7B7B] font-semibold mt-0.5">to me</p>
              </div>

              <div className="text-[10px] text-[#7B7B7B] font-semibold whitespace-nowrap">9:41 AM</div>
            </div>

            {/* Subject — also resolve vars */}
            <h1 className="mt-4 text-[15px] leading-tight font-semibold text-gray-900 break-words">
              {resolveVars(subject)}
            </h1>

            {previewText && (
              <p className="text-xs text-gray-600 mt-1 line-clamp-2 break-words">
                {resolveVars(previewText)}
              </p>
            )}
          </div>

          {/* Email Body - Responsive container */}
          <div
            className="px-4 py-5 text-[13px] text-gray-800 leading-relaxed prose prose-sm max-w-none overflow-x-auto"
            style={{ 
              fontFamily: "system-ui, -apple-system, sans-serif",
              wordBreak: "break-word",
              overflowWrap: "break-word",
              maxWidth: "100%"
            }}
            dangerouslySetInnerHTML={{ __html: displayHtml }}
          />

          {senderName && (
            <div className="px-4 pb-6 text-[11px] text-gray-500 border-t border-gray-100 pt-4">
              Sent with ❤️ by {senderName}
            </div>
          )}
        </div>

        {/* Bottom Navigation Bar */}
        <div className="bg-white border-t border-gray-200 pt-2 flex items-center justify-center gap-3 text-black text-sm flex-wrap">
          <button className="hover:bg-gray-100 p-2 border rounded-xl min-w-[60px]">Reply</button>
          <button className="hover:bg-gray-100 p-2 border rounded-xl min-w-[60px]">Forward</button>
        </div>

        {/* Phone Home Indicator */}
        <div className="h-5 bg-white flex items-center justify-center">
          <div className="w-20 h-1 bg-gray-800 rounded-full" />
        </div>
      </div>
    </div>
  );
};